import { Group, SegmentedControl, Select } from '@byecode/ui'
import { IndependentAction } from '@lighthouse/core'
import type { FlowNode, NodeTypes, OpenLinkActionConfig, VariableTree2 } from '@lighthouse/shared'
import {
    getAllCanBeUpstreamNodes,
    getFormDatasourceOptions,
    getImageVariableOption,
    getInputInnerTypeMatch,
    getUpstreamActionsOptions,
    getUpstreamNodesOptions,
    getViewVariableOption,
    ListItem4ByecodeUi,
    NodeFieldPreview,
    nodeTypeOptions,
    PAGE_LINK_OPTION,
    useAtomData
} from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'

import { lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useActionAdderDepParams } from '@/hooks/useActionAdderDepParams'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

import { LINK_TARGET_OPTIONS } from '../constant'
import * as SC from '../styles'

interface OpenLinkActionConfigureProps {
    nodeType?: NodeTypes
    value: OpenLinkActionConfig
    onChange: (value: OpenLinkActionConfig) => void
    allParentNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    actionTypeSwitcher?: React.ReactNode
}

export const OpenLinkActionConfigure: React.FC<OpenLinkActionConfigureProps> = ({
    nodeType,
    value,
    onChange,
    allParentNodes,
    allParentActions,
    actionTypeSwitcher
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const disabledWithVersion = useIsDisabledWithVersion()
    const { viewDsId = '', formDsId = '', viewType, isImage } = useActionAdderDepParams()
    const formDataSource = useDataSource(appId, envId, formDsId)
    const viewDataSource = useDataSource(appId, envId, viewDsId)
    const { type } = useFlowContainerContext()
    const isInFlow = !!type
    const isNotAutomation = type !== 'automation'

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => (isInFlow ? '' : s?.pageId || ''), [isInFlow])
    )

    const upstreamOptions = useMemo(() => {
        if (isInFlow) {
            return getUpstreamNodesOptions({
                workflowList,
                upstreamNodes: getAllCanBeUpstreamNodes(allParentNodes || []),
                allUpstreamNodes: allParentNodes,
                dataSourceList
            })
        }
        return getUpstreamActionsOptions({
            workflowList,
            allParentActions: allParentActions || [],
            dataSourceList
        })
    }, [allParentActions, allParentNodes, dataSourceList, isInFlow, workflowList])

    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })

    const formOption = useMemo(() => {
        if (formDataSource && !isInFlow) {
            return getFormDatasourceOptions({ dataSource: formDataSource })
        }
    }, [formDataSource, isInFlow])

    const imageOption = useMemo(() => {
        if (isImage) {
            return getImageVariableOption()
        }
    }, [isImage])

    const { customViewOption } = useVariableCustomViewOption()

    const viewOptions = useMemo(() => {
        if (isInFlow) {
            return
        }

        const options: VariableTree2[] = []
        if (viewDataSource) {
            const viewOption = getViewVariableOption({
                dataSource: viewDataSource,
                viewType
            })
            if (viewOption) {
                options.push(viewOption)

                return options
            }
        }
        if (customViewOption) {
            options.push(customViewOption)
        }

        return options
    }, [isInFlow, customViewOption, viewDataSource, viewType])

    const userOption = useGetUserRecordOptions()

    return (
        <SC.Container>
            <Group label="动作配置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>类型</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>链接</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            autoHeight
                            value={value.link}
                            onBlur={link => onChange({ ...value, link })}
                            placeholder="请输入"
                            viewOptions={viewOptions}
                            formOption={formOption}
                            pageOptions={isNotAutomation ? [PAGE_LINK_OPTION, ...(pageOptions || [])] : undefined}
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            dataSourceList={dataSourceList}
                            imageOption={imageOption}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>打开方式</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <SegmentedControl
                            value={value.target || '_self'}
                            onChange={target => onChange({ ...value, target })}
                            fullWidth
                            data={LINK_TARGET_OPTIONS}
                            style={{ width: 180 }}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>
        </SC.Container>
    )
}
